import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import { getScoreColorHex } from '../../utils/scoreUtils';
import { marketingPalette } from '../../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../common/PublicPageFrame';

const palette = marketingPalette;

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

function formatDate(dateString) {
  if (!dateString) return 'Date unavailable';

  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDurationMinutes(minutes) {
  const numeric = Number(minutes);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric);
}

function RecentHistories({ histories, loading, onViewAll }) {
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 2.4, md: 3 }, height: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
        alignItems={{ sm: 'flex-start' }}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography
            sx={{
              color: palette.heroBlueMid,
              fontSize: '0.8rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: publicBodyFontFamily,
            }}
          >
            Recent activity
          </Typography>
          <Typography
            sx={{
              mt: 0.8,
              color: palette.ink,
              fontSize: { xs: '1.3rem', md: '1.55rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              fontFamily: publicHeadingFontFamily,
            }}
          >
            Recent cases
          </Typography>
          <Typography
            sx={{
              mt: 0.8,
              color: palette.inkSoft,
              lineHeight: 1.68,
              fontFamily: publicBodyFontFamily,
            }}
          >
            Jump back into your latest marked consultations.
          </Typography>
        </Box>

        <Button
          onClick={onViewAll}
          sx={{
            ...publicSecondaryButtonSx,
            minHeight: 44,
            alignSelf: { xs: 'stretch', sm: 'flex-start' },
          }}
        >
          View all cases
        </Button>
      </Stack>

      {loading ? (
        <Box
          sx={{
            minHeight: 260,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading recent cases...
          </Typography>
        </Box>
      ) : histories && histories.length > 0 ? (
        <Stack spacing={1.5}>
          {histories.map((history) => {
            const score = Number(history?.overallPercentage) || 0;
            const duration = formatDurationMinutes(history?.duration);
            const scoreColor = getScoreColorHex(score);

            return (
              <Box
                key={history._id}
                sx={{
                  borderRadius: '22px',
                  border: `1px solid ${alpha(palette.ink, 0.07)}`,
                  bgcolor: alpha(palette.heroBlueMist, 0.52),
                  p: 2,
                }}
              >
                <Stack spacing={1.35}>
                  <Stack direction="row" justifyContent="space-between" spacing={1.25} alignItems="flex-start">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          color: palette.ink,
                          fontWeight: 800,
                          lineHeight: 1.35,
                          fontFamily: publicBodyFontFamily,
                          wordBreak: 'break-word',
                        }}
                      >
                        {history.title || 'Untitled case'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${score.toFixed(1)}%`}
                      sx={{
                        bgcolor: alpha(scoreColor, 0.14),
                        color: scoreColor,
                        borderRadius: '999px',
                        fontWeight: 800,
                        fontFamily: publicBodyFontFamily,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<CalendarMonthRoundedIcon sx={{ color: 'inherit !important' }} />}
                      label={formatDate(history.completedAt)}
                      sx={{
                        bgcolor: alpha('#ffffff', 0.75),
                        color: palette.inkSoft,
                        borderRadius: '999px',
                        fontWeight: 700,
                        fontFamily: publicBodyFontFamily,
                      }}
                    />
                    {duration !== null ? (
                      <Chip
                        icon={<TimerRoundedIcon sx={{ color: 'inherit !important' }} />}
                        label={`${duration} min`}
                        sx={{
                          bgcolor: alpha('#ffffff', 0.75),
                          color: palette.inkSoft,
                          borderRadius: '999px',
                          fontWeight: 700,
                          fontFamily: publicBodyFontFamily,
                        }}
                      />
                    ) : null}
                  </Stack>

                  <Button
                    component={RouterLink}
                    to={`/history/${history._id}`}
                    endIcon={<ArrowOutwardRoundedIcon />}
                    sx={{
                      ...publicPrimaryButtonSx,
                      minHeight: 42,
                      alignSelf: 'flex-start',
                      px: 2.1,
                    }}
                  >
                    Review
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Box
          sx={{
            minHeight: 260,
            borderRadius: '22px',
            border: `1px dashed ${alpha(palette.ink, 0.14)}`,
            bgcolor: alpha(palette.heroBlueMist, 0.65),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 3,
          }}
        >
          <Box
            sx={{
              width: 62,
              height: 62,
              mb: 1.5,
              borderRadius: '22px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(palette.heroBlueMid, 0.10),
              color: palette.heroBlueDeep,
            }}
          >
            <DescriptionRoundedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              color: palette.ink,
              fontWeight: 800,
              mb: 0.8,
              fontFamily: publicBodyFontFamily,
            }}
          >
            No completed cases yet
          </Typography>
          <Typography
            sx={{
              maxWidth: 360,
              color: palette.inkSoft,
              lineHeight: 1.68,
              fontFamily: publicBodyFontFamily,
            }}
          >
            Once you complete and mark a consultation, it will appear here for quick review.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default RecentHistories;
